import React, { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { useCustomer } from '@techstyle/react-accounts';
import { FormattedMessage } from '@techstyle/react-intl';
import {
  updateSessionDetail,
  useSession,
  useDomain,
} from '@techstyle/redux-core';

import {
  loadRecommendedProducts,
  trackPdpCrossSellClick,
} from '../../actions/products';
import { getWearItWithProducts } from '../../utils/getWearItWithProducts';
import { isProductSet, isClearanceItem } from '../../utils/selectors';
import { useLoadProducts } from '../../utils/useLoadProducts';
import { useMembership } from '../../utils/useMembership';
import { Component as ProductCell } from '../ProductCell';
import { Component as ProductRecommendations } from '../ProductRecommendations';

// Prevent refetches for 5 minutes on the recommended products.
const STALE_TIME = 1000 * 60 * 5;

const StyledProductRecommendations = styled(ProductRecommendations)`
  ${props =>
    props.variantStyle &&
    css`
      ${props =>
        !props.shouldUseBmigComponents &&
        `${ProductCell.PromoAsset} {
        font-size: 10px;
      }
    `}

      @media (min-width: ${props => props.theme.breakpoints.large - 1}px) {
        .slick-list {
          margin-left: 33px;
          margin-right: 33px;
        }
        .slick-slide {
          > div {
            padding: 0 0 0 ${props => props.theme.spacing.tiny}px;
            text-align: left;
          }
        }
        .slick-arrow {
          font-size: 4px;
          color: ${props => props.theme.colors.active};
          border-radius: 14.4px;
          border: solid 1px ${props => props.theme.colors.lavender250};
          background-color: ${props => props.theme.colors.white};
          margin-top: -25px;
          width: 25px;
          height: 25px;
          text-align: center;
        }
        svg {
          width: 10px;
          height: 12px;
        }
      }
      @media (max-width: ${props => props.theme.breakpoints.large - 1}px) {
        .slick-list {
          overflow: visible;
        }
        .slick-slider {
          padding-bottom: 40px; /* Account for dots */
          margin-left: ${props =>
            props.theme.spacing.tiny * -1}px; /* Account for slide margin */
        }
        .slick-slide {
          > div {
            padding: 0 4px;
            text-align: left;
          }
        }
      }

      .slick-dots li.slick-active {
        button:before {
          background: ${props => props.theme.colors.default};
          /* Intensify the light border in case we're on top of a dark image. */
          box-shadow: 0 0 0 1px ${props => props.theme.colors.default};
        }
      }

      .slick-disabled {
        display: none !important;
        pointer-events: none;
      }
    `}
`;

const { vipOnly, leadOnly, hideFromGrid, fplOnly } =
  config.get('public.productTags');

const ProductDetailRecommendations = ({
  maxProducts = 36,
  includeSaleItems = false,
  hidePromo = false,
  hidePrice = false,
  handleProductClick, // in case we re-use this component that is already using this callBack
  source = 'wear_it_with',
  pSource = source,
  title = (
    <FormattedMessage
      id="site_pdp.product_recommendations_title"
      defaultMessage="Wear It With"
    />
  ),
  variantStyle = false,
  shouldUseBmigComponents = false,
  product,
  options,
  trackingSource,
  additionalParam,
  shouldNotBeClickable,
  ...rest
}) => {
  const dispatch = useDispatch();

  const { tld } = useDomain();

  const state = useSelector(state => state);

  const { isVip, isLead } = useMembership();
  const { isVisitor } = useSession();
  const profile = useCustomer();
  const { optionSignatures = {}, networkStatus: customerNetworkStatus } =
    profile;

  const isCav = pSource === 'customer_also_viewed';
  const masterProductId = product?.master_product_id;

  // We only load products using `loadRecommendedProducts` when a `product`
  // prop is passed in. We also don't load products when the product is a
  // product set.
  const { shouldLoadRecommendedProducts, shouldLoadAnyProducts } =
    useMemo(() => {
      if (product) {
        if (source === 'recommended' && !(isCav && isProductSet(product))) {
          return {
            shouldLoadRecommendedProducts: true,
            shouldLoadAnyProducts: true,
          };
        }
        return {
          shouldLoadRecommendedProducts: false,
          shouldLoadAnyProducts: false,
        };
      }

      return {
        shouldLoadRecommendedProducts: false,
        shouldLoadAnyProducts: true,
      };
    }, [isCav, product, source]);

  const getRecommendedProducts = useCallback(
    masterProductId => () => {
      return dispatch(
        loadRecommendedProducts(masterProductId, {
          size: 50,
          includePromos: true,
        })
      );
    },
    [dispatch]
  );

  const {
    data: recommendedProductData,
    isLoading: isRecommendedProductsLoading,
  } = useQuery({
    enabled: shouldLoadRecommendedProducts,
    queryKey: ['recommendedProducts', masterProductId],
    queryFn: getRecommendedProducts(masterProductId),
    staleTime: STALE_TIME,
  });

  const productFilters = useMemo(() => {
    const sizes = optionSignatures
      ? {
          braSizes: optionSignatures['bra-size'] || [],
          braletteSizes: optionSignatures['bralette-size'] || [],
          lingerieSizes: optionSignatures['lingerie-sleep-size'] || [],
          undieSizes: optionSignatures['bottom-size'] || [],
        }
      : {};

    const additionalParams = product ? {} : additionalParam;

    if (recommendedProductData?.payload) {
      const { products } = recommendedProductData.payload;
      const masterProductIds = products.map(product => {
        return product.master_product_id;
      });
      additionalParams.masterProductIds = masterProductIds;
    }

    return {
      ...sizes,
      includeOutOfStock: false,
      ...additionalParams,
      includeSaleItems,
    };
  }, [
    optionSignatures,
    product,
    additionalParam,
    recommendedProductData,
    includeSaleItems,
  ]);

  // If we're loading recommended products, we need to wait.
  const shouldLoadProducts = useMemo(() => {
    if (!shouldLoadAnyProducts) {
      return false;
    }

    if (
      (shouldLoadRecommendedProducts && isRecommendedProductsLoading) ||
      !customerNetworkStatus.isUpToDate
    ) {
      return false;
    }

    return true;
  }, [
    customerNetworkStatus,
    isRecommendedProductsLoading,
    shouldLoadAnyProducts,
    shouldLoadRecommendedProducts,
  ]);

  const { isLoading, data: productsData } = useLoadProducts({
    filters: productFilters,
    options: {
      shouldCategorize: true,
    },
    enabled: shouldLoadProducts,
    staleTime: STALE_TIME,
  });

  const recommendedProducts = useMemo(() => {
    if (isLoading || !productsData?.payload) {
      return [];
    }
    const { products } = productsData.payload;

    if (isCav) {
      /**
       * I create a map of products so I can present the cav list in the correct
       * scoring order that comes from the algo
       */
      const productMap = products.reduce((map, product) => {
        map[product.master_product_id] = product;
        return map;
      }, {});

      return productFilters.masterProductIds?.reduce(
        (list, masterProductId) => {
          const product = productMap[masterProductId];

          if (product) {
            list.push(product);
          }

          return list;
        },
        []
      );
    }

    return products.sort(
      (a, b) =>
        new Date(b.date_expected).getTime() -
        new Date(a.date_expected).getTime()
    );
  }, [productsData, isCav, isLoading, productFilters]);

  const handleRecommendedClick = useCallback(() => {
    if (handleProductClick) {
      handleProductClick();

      if (pSource === 'grid_recommendation') {
        dispatch(
          updateSessionDetail({ name: 'grid_recs_action_clicked', value: '' })
        );
      }
    }
    if (trackingSource) {
      dispatch(trackPdpCrossSellClick(trackingSource)); // this is currently tracking WIW, CAV and YMAL
    }
  }, [dispatch, handleProductClick, pSource, trackingSource]);

  const products = useMemo(() => {
    if (!recommendedProducts || !recommendedProducts.length) {
      return [];
    }
    let products;
    // FIXME: Hopefully we can undo this later. Sometimes `wear_it_with` comes
    // back as a single object, not an array of objects.
    if (!Array.isArray(recommendedProducts)) {
      products = [recommendedProducts];
    } else {
      products = [...recommendedProducts];
    }

    const filterList = [fplOnly];

    if (!['gwp'].includes(source)) {
      filterList.push(hideFromGrid);
    }

    if (isVip) {
      filterList.push(leadOnly);
    } else if (isLead || isVisitor) {
      filterList.push(vipOnly);
    }

    products = products.filter(
      ({ tag_id_list: tagIdList = [], label }) =>
        !isClearanceItem(state, { product }) &&
        !tagIdList.some(tagId => filterList.includes(tagId))
    );

    if (isCav) {
      const wearItWithProducts = getWearItWithProducts(product, {
        domain: tld,
        isVip,
      });

      products = products.filter(
        ({ master_product_id: masterProductId }) =>
          product.master_product_id !== masterProductId &&
          !wearItWithProducts.some(
            ({ master_product_id: mPId }) => mPId === masterProductId
          )
      );
    }

    return products;
  }, [
    isCav,
    isLead,
    isVip,
    isVisitor,
    product,
    recommendedProducts,
    source,
    state,
    tld,
  ]);

  if (!shouldLoadAnyProducts) {
    return false;
  }

  return products.length ? (
    <StyledProductRecommendations
      options={options}
      onProductClick={handleRecommendedClick}
      products={products}
      hidePrice={hidePrice}
      hidePromo={hidePromo}
      maxProducts={maxProducts}
      title={title}
      variantStyle={variantStyle}
      shouldNotBeClickable={shouldNotBeClickable}
      trackingSource={trackingSource}
      pSource={pSource}
      shouldUseBmigComponents={shouldUseBmigComponents}
      {...rest}
    />
  ) : null;
};

ProductDetailRecommendations.propTypes = {
  product: PropTypes.object,
  options: PropTypes.object,
  hidePrice: PropTypes.bool,
  hidePromo: PropTypes.bool,
  includeSaleItems: PropTypes.bool,
  maxProducts: PropTypes.number,
  pSource: PropTypes.string,
  source: PropTypes.oneOf([
    'wear_it_with',
    'recommended',
    'grid_recommendation',
    'gwp',
  ]),
  title: PropTypes.node,
  trackingSource: PropTypes.string,
  variantStyle: PropTypes.bool,
  additionalParam: PropTypes.object,
  handleProductClick: PropTypes.oneOfType([PropTypes.func, () => null]),
  shouldNotBeClickable: PropTypes.bool,
  shouldUseBmigComponents: PropTypes.bool,
};

export default ProductDetailRecommendations;
